import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Wrench } from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Seo from "../components/Seo";
import Footer from "../components/Footer";

export default function Sponsorship() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/public/sponsorship")
      .then((r) => setData(r.data))
      .catch(() => setErr("Kunde inte hämta sponsringsdata"));
  }, []);

  if (err) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-white/60">{err}</div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-white/50">Laddar...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Seo
        title="Sponsring via fotoköp — Foton Enduroskola"
        description="När du köper enduro-foton här kan du välja att skänka intäkterna till en förare. Fotografen får 90 %, du stöttar sporten."
        path="/sponsring"
      />
      <Header />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <p className="label-overline mb-3">Sponsring</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-6xl leading-[0.95] mb-6 max-w-4xl">
          När du köper foton här <span className="text-brand">stöttar du förare.</span>
        </h1>
        <p className="text-lg text-white/70 max-w-2xl">
          Många av våra fotografer väljer att dela ut sina intäkter vidare till en förare. Enduroskola hanterar logistiken så föraren får sin sponsring. Win Win! Allt för sporten Enduro.
        </p>
      </section>

      {/* Totals */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label="Inkommit till sponsorpotten" value={data.total_credited} accent />
          <Stat label="Utbetalat till förare" value={data.total_spent} />
          <Stat label="Redo att användas" value={data.total_balance} accent />
        </div>
      </section>

      {/* Public riders */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="label-overline">Vem stöttar vi</p>
            <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl mt-2">Förare i sponsringspotten</h2>
          </div>
        </div>

        {data.riders.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <Heart className="w-10 h-10 text-brand mx-auto mb-3" />
            <p className="text-white/70">
              Inga publika sponsringar registrerade ännu. Fotografer som valt att publicera sin sponsring visas här.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-riders">
            {data.riders.map((r, i) => (
              <div key={i} className="card-surface p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-brand/20 flex items-center justify-center font-display font-black text-brand text-xl">
                    {r.rider_bib || "?"}
                  </div>
                  <div>
                    <p className="font-display font-bold uppercase text-white">{r.rider_name}</p>
                    {r.rider_club && <p className="text-xs text-white/60">{r.rider_club}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-white/50 uppercase">In</p>
                    <p className="font-display font-bold text-white">{r.credited.toFixed(0)} kr</p>
                  </div>
                  <div>
                    <p className="text-white/50 uppercase">Ut</p>
                    <p className="font-display font-bold text-white">{r.spent.toFixed(0)} kr</p>
                  </div>
                  <div>
                    <p className="text-white/50 uppercase">Kvar</p>
                    <p className="font-display font-bold text-brand">{r.balance.toFixed(0)} kr</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {data.anonymous_total > 0 && (
          <p className="text-white/50 text-sm mt-6" data-testid="anonymous-total">
            Utöver ovan har <span className="text-brand font-bold">{data.anonymous_total.toFixed(0)} kr</span> från {data.anonymous_count} anonyma sponsrings-åtaganden samlats till förare som valt att inte publicera namnet.
          </p>
        )}
      </section>

      {/* Recent payouts */}
      {data.recent_payouts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="label-overline">Senaste inköpen</p>
          <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl mt-2 mb-6">Transparent redovisning</h2>
          <div className="space-y-2">
            {data.recent_payouts.map((p) => (
              <div key={p.id} className="card-surface p-4 flex items-center gap-4">
                <Wrench className="w-5 h-5 text-brand flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white truncate">{p.description}</p>
                  <p className="text-xs text-white/50">
                    {p.rider_name}{p.rider_bib ? ` #${p.rider_bib}` : ""} · {new Date(p.created_at).toLocaleDateString("sv-SE")}
                  </p>
                </div>
                {p.receipt_url && (
                  <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-brand hover:underline text-sm">Kvitto</a>
                )}
                <p className="font-display font-bold text-white whitespace-nowrap">−{p.amount.toFixed(0)} kr</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-[#0d0d0d] border-y border-white/5 my-12 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display font-black uppercase text-white text-3xl sm:text-5xl mb-4">
            Vill du sponsra en förare?
          </h2>
          <p className="text-white/70 mb-6 max-w-2xl mx-auto">
            Registrera dig som fotograf, ladda upp bilder från event du är på, och peka ut en förare du tror på. Varje köpt bild går till en pott som används för deras utrustning.
          </p>
          <Link to="/photographer/register" className="btn-primary inline-flex items-center gap-2" data-testid="cta-register-photographer">
            Bli sponsor-fotograf
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`card-surface p-6 ${accent ? "border-brand/40" : ""}`}>
      <p className={`text-4xl font-display font-black ${accent ? "text-brand" : "text-white"}`}>
        {(value || 0).toLocaleString("sv-SE", { maximumFractionDigits: 0 })} kr
      </p>
      <p className="text-xs uppercase tracking-wider text-white/50 mt-1">{label}</p>
    </div>
  );
}
